import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { OverlayTrigger, Tooltip, Table, Form, Col, Container, Row } from "react-bootstrap";
import { Formik, Form as FormFormik } from "formik";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import { Pagination, WithModulesAccess } from "@services/ui-components";
import DurationInput from "../../../components/ui-components/DurationInput/DurationInput";
import PriceComponent from "../../ui-components/PriceComponent/PriceComponent";
import EmployeeCell from "@services/ui-components/src/EmployeeCell/EmployeeCell";
import SalaryComponent from "../../ui-components/SalaryComponent/SalaryComponent";
import ServicesModalConfirmation from "../../ui-components/ServicesModalConfirmation/ServicesModalConfirmation";
import ToastComponent from "../../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../../hooks/useToast";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { debounce } from "@me-team/host/src/utils/utils";
import SocketConnection from "../../../services/SocketConnection";
import {
    useGetEmployeeServiceRelationsByEmployeeIdQuery,
    useGetServicesProvidersQuery,
} from "@me-team/host/main/servicesProviders/graphql/servicesProviders.hooks";
import { useGetCurrencySignQuery } from "@me-team/host/main/appointment/graphql/appointment.hooks";
import {
    useCreateAllRelationsMutation,
    useCreateEmployeeServiceRelationMutation,
    useDeleteAllRelationsMutation,
    useDeleteEmployeeServiceRelationMutation,
    useUpdateEmployeeServiceRelationMutation,
} from "@me-team/host/main/employeeServices/graphql/employeeServices.hooks";
import {
    CompanyAccess,
    EmployeeProxy,
    EmployeeServiceRelationProxy,
    PermissionScope,
    SalaryType,
    ServiceEmployeesPaginated,
    ServiceProxy,
} from "@me-team/host/main/graphql/types";
import ExistingBookingsModal from "../../EmployeeServices/EmployeeServicesTable/ExistingBookingsModal";
import JoyrideWrapper from "../../Joyride/JoyrideWrapper";
import { joyrideService } from "../../Joyride/joyrideService";
import { getRowClassName, setJoyrideUser } from "../constants";
import ErrorService from "../../../services/ErrorService";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { currentBranchEmployeeIdVar, currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { AllModulesTagName } from "../../../utils/enums";
import { Filter } from "../ServiceProviders";

interface ServiceProvidersTableProps {
    filter: Filter;
    setServiceName: React.Dispatch<React.SetStateAction<string | undefined>>;
}
const ServiceProvidersTable: React.FC<ServiceProvidersTableProps> = ({ filter, setServiceName }) => {
    const { t } = useTranslation();
    const { id, branchId } = useParams();
    const checkboxRef = useRef(null);
    const ENTITY_IMPORT_TYPE: string = "service-providers";
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [show, setShow] = useState(false);
    const [text, setText] = useState(" ");
    const [setIsLoadingRefetchServices] = useState<boolean>(false);
    const [showExistingBooking, setShowExistingBooking] = useState(false);
    const runJoy = localStorage.getItem("run") === "true";
    const step = localStorage.getItem("step") || "0";
    const [stepIndex, setStepIndex] = useState(+step);
    const [run] = useState(runJoy);
    const canEditServiceEmployeeRelations = useRoleAccess(CompanyAccess.ServiceEmployeesEdit);
    const canViewEmployeeAll = useRoleAccess(CompanyAccess.EmployeeView, { scope: PermissionScope.All });
    const canViewEmployeeSelf = useRoleAccess(CompanyAccess.EmployeeView, { scope: PermissionScope.Self });
    const canEditEmployeeAll = useRoleAccess(CompanyAccess.EmployeeEdit, { scope: PermissionScope.All });
    const canEditEmployeeSelf = useRoleAccess(CompanyAccess.EmployeeEdit, { scope: PermissionScope.Self });
    const canViewSalaryAll = useRoleAccess(CompanyAccess.SalarySettingsView, { scope: PermissionScope.All });
    const canViewSalarySelf = useRoleAccess(CompanyAccess.SalarySettingsView, { scope: PermissionScope.Self });
    const canEditSalaryAll = useRoleAccess(CompanyAccess.SalarySettingsEdit, { scope: PermissionScope.All });
    const canEditSalarySelf = useRoleAccess(CompanyAccess.SalarySettingsEdit, { scope: PermissionScope.Self });
    const canAccessSalarySettings = canViewSalaryAll || canViewSalarySelf || canEditSalaryAll || canEditSalarySelf;
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const { showToast, toggleShowToast, setShowToast } = useToast();
    const joyrideServiceUser = joyrideService.getUser();
    const [currentUser, setCurrentUser] = useState(null);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);

    useEffect(() => {
        if (!run) {
            joyrideService.clearUser();
            setCurrentUser(null);
            return;
        }
        const updatedUser = setJoyrideUser(joyrideServiceUser, !!run, currentBranchEmployeeId);
        setCurrentUser(updatedUser);
    }, [run, currentBranchEmployeeId, joyrideServiceUser]);

    const { data, loading, refetch } = useGetServicesProvidersQuery({
        variables: {
            serviceId: +id,
            page: currentPage,
            itemsPerPage: itemsPerPage,
            employeePosition: filter.positionId,
            employeeName: filter.name,
            companyId: +currentCompanyId,
            employeeId: currentUser ? currentUser?.id : null,
            branchId: filter.branchId !== -1 ? filter.branchId : Number(branchId),
            filterByCompany: filter.branchId === -1,
        },
        onCompleted: (data) => {
            setServiceName(data?.user.company.services.services[0].name);
        },
    });
    const { data: allServicesData, loading: allServicesLoading } = useGetServicesProvidersQuery({
        variables: {
            serviceId: +id,
            page: 1,
            itemsPerPage: 10000000,
            employeePosition: filter.positionId,
            employeeName: filter.name,
            companyId: +currentCompanyId,
            branchId: +branchId,
            filterByCompany: true,
        },
    });

    const {
        data: employeeServiceRelationsByIdData,
        loading: loadingEmployeeById,
        refetch: refetchEmployeeById,
    } = useGetEmployeeServiceRelationsByEmployeeIdQuery({
        variables: {
            serviceId: +id,
            companyId: +currentCompanyId,
            employeeId: currentUser?.id,
        },
        skip: !currentUser?.id || !currentCompanyId,
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedRefetch = useCallback(
        debounce(() => {
            setIsLoadingRefetchServices(true);
            refetch().then(() => {
                setIsLoadingRefetchServices(false);
            });
        }, 1000),
        [refetch]
    );

    useEffect(() => {
        if (currentCompanyId) {
            new SocketConnection(`${process.env.REACT_APP_SOCKET}/?companyId=${currentCompanyId}`, (data: any) => {
                if (data.entity === ENTITY_IMPORT_TYPE) {
                    debouncedRefetch();
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCompanyId, refetch]);
    const selectAllData = data?.user?.company?.employeesList;

    useDocumentTitle(`${t("Service Providers")} «${data?.user.company.services.services[0].name || " "} »`);
    const { data: currency, loading: currencyLoading } = useGetCurrencySignQuery({
        variables: { companyId: +currentCompanyId },
    });
    const currencySign = currency?.user?.company?.currency?.currencySign;
    const servicesProviders: ServiceEmployeesPaginated = data?.user?.company?.services?.services[0]?.serviceEmployees;
    const duration = data?.user?.company?.services?.services[0]?.duration?.slice(0, -3);
    const hasDeletedRelations = servicesProviders?.serviceEmployees?.some((service) =>
        service.employeeServiceRelations?.some((relation) => relation.deleted === true)
    );

    const pageCount = servicesProviders?.pageCount;
    const totalItems = servicesProviders?.totalCount || 0;
    const firstItemIndex = (currentPage - 1) * itemsPerPage + 1;
    const lastItemIndex = Math.min(currentPage * itemsPerPage, totalItems);
    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };
    const allServices = allServicesData?.user?.company?.services?.services[0]?.serviceEmployees?.serviceEmployees;
    const employeeById =
        employeeServiceRelationsByIdData &&
        employeeServiceRelationsByIdData?.user?.company?.employees?.employees?.[0].employeeServiceRelations?.[0];

    const isBooleanValue: boolean = currentUser
        ? Boolean(employeeById?.id)
        : Boolean(servicesProviders?.serviceEmployees?.[0]?.employeeServiceRelations?.length);

    const isOnlineEmployee: boolean = currentUser
        ? employeeById?.online
        : servicesProviders?.serviceEmployees?.[0]?.employeeServiceRelations?.[0]?.online;

    const [updateServices] = useUpdateEmployeeServiceRelationMutation({
        onError: (error) => {
            console.error("Error occurred during mutation:", error);
        },
        context: {
            errorType: "local",
        },
    });
    const [createRelation] = useCreateEmployeeServiceRelationMutation({
        onError: (error) => {
            console.error("Error occurred during mutation:", error);
        },
        context: {
            errorType: "local",
        },
    });
    const [deleteRelation] = useDeleteEmployeeServiceRelationMutation({
        onError: (error) => {
            console.error("Error occurred during mutation:", error);
        },
        context: {
            errorType: "local",
        },
    });
    const handleToggleModal = () => setShow((prevState) => !prevState);
    const [createAllRelations] = useCreateAllRelationsMutation();
    const [deleteAllRelation] = useDeleteAllRelationsMutation();

    const checkEmployeesList = useMemo(() => {
        return selectAllData?.every((service) => service.employeeServiceRelations.length > 0) || false;
    }, [selectAllData]);

    const filteredServices = allServices?.filter(
        (i) => i?.employeeServiceRelations.length > 0 && !i?.employeeServiceRelations[0]?.deleted
    );
    const isIndeterminate = filteredServices?.length > 0 && totalItems > 0 && totalItems !== filteredServices?.length;

    const changeRelationForAll = async () => {
        const operation = checkEmployeesList || isIndeterminate ? deleteAllRelation : createAllRelations;
        await operation({
            variables: {
                input: {
                    service: +id,
                },
                companyId: +currentCompanyId,
                branchId: +branchId,
                employeeId: Number(currentBranchEmployeeId),
            },
            onCompleted: () => {
                toggleShowToast();
                refetch();
            },
            onError: (error) => {
                const graphQLError = error.graphQLErrors?.[0];
                if (graphQLError) {
                    const category = graphQLError.extensions?.category;
                    if (category === "employee_service_relation.has_bookings_in_future") {
                        checkboxRef.current.indeterminate = isIndeterminate;
                        setShowExistingBooking(true);
                        return;
                    }
                }
            },
        });
    };

    useEffect(() => {
        if (checkboxRef.current) {
            checkboxRef.current.indeterminate = isIndeterminate;
        }
    }, [isIndeterminate]);
    const updateEmployeeServices = async (values: EmployeeServiceRelationProxy) => {
        const variables = {
            duration: values.duration,
            price: values.price,
            onlineBooking: values.online,
            salaryType: values.salaryType === 1 ? SalaryType.Percent : SalaryType.Currency,
            salaryValue: values.salaryValue ?? data?.user.company.services.services[0].price,
        };

        try {
            await updateServices({
                variables: {
                    relationId: values.id,
                    input: variables,
                    companyId: +currentCompanyId,
                    branchId: +branchId,
                    employeeId: Number(currentBranchEmployeeId),
                },
            });
            toggleShowToast();
            refetch();
            refetchEmployeeById();
        } catch (error) {
            console.error("Update error", error);
        }
    };
    const deleteServiceRelation = async (values: EmployeeProxy) => {
        const resp = await deleteRelation({
            variables: {
                id: values.employeeServiceRelations[0].id,
                companyId: +currentCompanyId,
                branchId: +branchId,
                employeeId: Number(currentBranchEmployeeId),
            },
        });
        if (resp?.errors?.message === "Relation has bookings in future and cannot be deleted") {
            setShowExistingBooking(true);
        } else {
            toggleShowToast();
            refetch();
        }
    };
    const createServiceRelation = async (values: EmployeeServiceRelationProxy) => {
        const variables = {
            employeeId: values.id,
            serviceId: +id,
            duration: data?.user.company.services.services[0].duration,
            price: data?.user.company.services.services[0].price,
            onlineBooking: false,
            companyId: +currentCompanyId,
            branchId: +branchId,
        };
        try {
            await createRelation({ variables });
            toggleShowToast();
            refetch();
            refetchEmployeeById();
        } catch (error) {
            console.error("Update error", error);
        }
    };
    const handleUpdateRelation = async (values: EmployeeProxy) => {
        values.employeeServiceRelations.length > 0 && !values?.employeeServiceRelations[0]?.deleted
            ? deleteServiceRelation(values)
            : createServiceRelation(values);
    };
    const updateOnlineBooking = (i: EmployeeProxy) => {
        setSelectedEmployee(i);
        const service = data?.user?.company?.services?.services[0].name;
        const employee = i;
        const onlineRegistration = data?.user.company.services.services[0]?.onlineRegistration;
        const isOnlineSignUpAvailable = i?.recordSetting?.isOnlineSignUpAvailable;
        switch (true) {
            case isOnlineSignUpAvailable === false && onlineRegistration !== false:
                setText(
                    `${t("Employee")} ${employee?.name} ${employee?.surname} ${t("is not available for Online booking form. Should we make employee")} ${employee?.name} ${employee?.surname} ${t("available in the Online booking form for your clients?")}`
                );
                break;
            case isOnlineSignUpAvailable === true && onlineRegistration === false:
                setText(
                    `${t("Service")} ${service} ${t("is not available for Online booking form. Should we make service")} ${service} ${t("available in the Online Booking form for your clients?")}`
                );
                break;
            case isOnlineSignUpAvailable === false && onlineRegistration === false:
                setText(
                    `${t("Service")} ${service} ${t("and employee")} ${employee?.name} ${employee?.surname} ${t("are not available for Online Booking form. Should we make")} ${service} ${t("and employee")} ${employee?.name} ${employee?.surname} ${t("available in the Online Booking form for your clients")} ?`
                );
                break;
            default:
                updateEmployeeServices({
                    ...i.employeeServiceRelations[0],
                    online: !i.employeeServiceRelations[0].online,
                });
                return;
        }
        !i.employeeServiceRelations[0].online
            ? handleToggleModal()
            : updateEmployeeServices({
                  ...i.employeeServiceRelations[0],
                  online: !i.employeeServiceRelations[0].online,
                  salaryType: i.employeeServiceRelations[0].salaryType === 1 ? SalaryType.Percent : SalaryType.Currency,
              });
    };
    const updateDuration = (i: ServiceProxy, duration) => {
        updateEmployeeServices({ ...i.employeeServiceRelations[0], duration: duration });
    };

    const canOpenEmployeeLink = useCallback(
        (employeeId?: number | null) => {
            if (typeof employeeId !== "number") return false;

            const isSelfEmployee = currentBranchEmployeeId === employeeId;

            return isSelfEmployee
                ? canViewEmployeeAll || canViewEmployeeSelf || canEditEmployeeAll || canEditEmployeeSelf
                : canViewEmployeeAll || canEditEmployeeAll;
        },
        [canViewEmployeeAll, canViewEmployeeSelf, canEditEmployeeAll, canEditEmployeeSelf, currentBranchEmployeeId]
    );

    if (loading || currencyLoading || allServicesLoading || loadingEmployeeById) return <Spinner />;

    return (
        <>
            <JoyrideWrapper
                run={run}
                stepIndex={stepIndex}
                setStepIndex={setStepIndex}
                booleanValue={isBooleanValue}
                info={isOnlineEmployee}
            />
            {servicesProviders?.serviceEmployees?.length === 0 ? (
                <Container className="mt-5">
                    <Row className="mt-5">
                        <Col xs={12}>
                            <h4 className="head-personal-data text-center fw-normal pt-5 mt-5">
                                {t("NoDataFoundForTheGivenValues")}
                            </h4>
                        </Col>
                    </Row>
                </Container>
            ) : (
                <>
                    <Table responsive className={run && window?.innerWidth > 930 ? "" : "overflow-auto"}>
                        <thead>
                            <tr>
                                <th className="col-1">
                                    <OverlayTrigger
                                        overlay={<Tooltip>{t("Select the providers of this service")}</Tooltip>}
                                    >
                                        <Form>
                                            <Form.Check
                                                type={"checkbox"}
                                                id={`serviceProviders-selectAll-field`}
                                                label={<i className="bi bi-info-circle"> </i>}
                                                checked={checkEmployeesList && !hasDeletedRelations}
                                                onChange={() => changeRelationForAll()}
                                                disabled={!canEditServiceEmployeeRelations}
                                                ref={checkboxRef}
                                            />
                                        </Form>
                                    </OverlayTrigger>
                                </th>
                                <th className="col-2">{t("Online Booking")}</th>
                                <th className="col-3">{t("Employee")}</th>
                                <th className="col-2">{t("Duration")}</th>
                                <th className="col-2">{t("Cost")}</th>
                                {canAccessSalarySettings ? (
                                    <WithModulesAccess modules={[AllModulesTagName.SALARY]}>
                                        <th className="col-3">{t("Salary")}</th>
                                    </WithModulesAccess>
                                ) : null}
                            </tr>
                        </thead>
                        {servicesProviders?.serviceEmployees?.length === 0 ? (
                            <Container className="mt-5">
                                <Row className="mt-5">
                                    <Col xs={12}>
                                        <h4 className="head-personal-data text-center fw-normal pt-5 mt-5">
                                            {t("NoDataFoundForTheGivenValues")}
                                        </h4>
                                    </Col>
                                </Row>
                            </Container>
                        ) : (
                            <tbody>
                                {servicesProviders?.serviceEmployees.map((i, index) => {
                                    const isSelfEmployee = currentBranchEmployeeId === i.id;
                                    const canViewSalary = isSelfEmployee
                                        ? canViewSalaryAll || canViewSalarySelf || canEditSalaryAll || canEditSalarySelf
                                        : canViewSalaryAll || canEditSalaryAll;
                                    const canEditSalary = isSelfEmployee
                                        ? canEditSalaryAll || canEditSalarySelf
                                        : canEditSalaryAll;
                                    const hasActiveEmployeeServiceRelation =
                                        i?.employeeServiceRelations?.length > 0 &&
                                        !i?.employeeServiceRelations[0]?.deleted;
                                    const canShowSalaryComponent =
                                        hasActiveEmployeeServiceRelation && (canViewSalary || canEditSalary);

                                    return (
                                        <tr key={i?.name + index}>
                                            <td className={getRowClassName(i, currentUser, index, "service-providers")}>
                                                <Form>
                                                    <Form.Check
                                                        type={"checkbox"}
                                                        id={`serviceProviders-${"updateRelation" + index}-field`}
                                                        checked={
                                                            i.employeeServiceRelations.length > 0 &&
                                                            !i?.employeeServiceRelations[0]?.deleted
                                                        }
                                                        onChange={() => handleUpdateRelation(i)}
                                                        disabled={!canEditServiceEmployeeRelations}
                                                    />
                                                </Form>
                                            </td>
                                            <td
                                                className={getRowClassName(
                                                    i,
                                                    currentUser,
                                                    index,
                                                    "service-providers-booking"
                                                )}
                                            >
                                                <Form>
                                                    <Form.Check
                                                        disabled={
                                                            !canEditServiceEmployeeRelations ||
                                                            i.employeeServiceRelations.length === 0 ||
                                                            !(
                                                                i.employeeServiceRelations.length > 0 &&
                                                                !i?.employeeServiceRelations[0]?.deleted
                                                            )
                                                        }
                                                        type="switch"
                                                        id={`serviceProviders-${"updateOnlineBooking" + index}-field`}
                                                        checked={
                                                            i.employeeServiceRelations.length > 0 &&
                                                            !i?.employeeServiceRelations[0]?.deleted &&
                                                            !!i?.employeeServiceRelations[0]?.online
                                                        }
                                                        onChange={() => updateOnlineBooking(i)}
                                                    />
                                                </Form>
                                            </td>
                                            <td>
                                                <EmployeeCell i={i} link={canOpenEmployeeLink(i?.id)} />
                                            </td>
                                            <td className="py-1">
                                                <div style={{ width: "94px" }}>
                                                    {i?.employeeServiceRelations[0]?.duration &&
                                                    !i?.employeeServiceRelations[0]?.deleted ? (
                                                        <Formik initialValues={{}} onSubmit={() => {}}>
                                                            {(formik) => (
                                                                <FormFormik onSubmit={formik.handleSubmit}>
                                                                    <DurationInput
                                                                        name="Name"
                                                                        label="label name"
                                                                        prevValue={
                                                                            i?.employeeServiceRelations[0]?.duration ||
                                                                            i?.duration
                                                                        }
                                                                        serviceValue={i}
                                                                        updateDuration={updateDuration}
                                                                        isDisabled={!canEditServiceEmployeeRelations}
                                                                    />
                                                                </FormFormik>
                                                            )}
                                                        </Formik>
                                                    ) : (
                                                        <span>{duration}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="py-1">
                                                {i?.employeeServiceRelations.length > 0 &&
                                                !i?.employeeServiceRelations[0]?.deleted ? (
                                                    <PriceComponent
                                                        service={i}
                                                        updateEmployeeServices={updateEmployeeServices}
                                                        currencySign={currencySign}
                                                        index={index}
                                                        isDisabled={!canEditServiceEmployeeRelations}
                                                    />
                                                ) : (
                                                    data?.user.company.services.services[0].price + " " + currencySign
                                                )}
                                            </td>
                                            {canAccessSalarySettings ? (
                                                <WithModulesAccess modules={[AllModulesTagName.SALARY]}>
                                                    <td className="py-1">
                                                        {canShowSalaryComponent ? (
                                                            <SalaryComponent
                                                                service={i?.employeeServiceRelations[0]}
                                                                updateEmployeeServices={updateEmployeeServices}
                                                                currencySign={currencySign}
                                                                index={index}
                                                                value={i?.employeeServiceRelations[0]?.salaryValue}
                                                                isDisabled={!canEditSalary}
                                                            />
                                                        ) : null}
                                                    </td>
                                                </WithModulesAccess>
                                            ) : null}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        )}
                    </Table>

                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <Pagination
                            t={t}
                            pages={Number(pageCount)}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                            firstItemIndex={firstItemIndex}
                            lastItemIndex={lastItemIndex}
                            totalItems={totalItems}
                        />
                    </div>
                </>
            )}

            <ServicesModalConfirmation
                show={show}
                handleToggleModal={handleToggleModal}
                selectedEmployee={selectedEmployee}
                updateFunc={updateEmployeeServices}
                text={text}
            />

            <ToastComponent show={showToast} setShow={setShowToast} text={t("Saved")} />
            <ExistingBookingsModal
                show={showExistingBooking}
                setShow={setShowExistingBooking}
                title="Appointments found for this service."
                bodyText="To disable all providers, go to the appointment journal and cancel the bookings."
            />
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default ServiceProvidersTable;
